import { InfoIcon } from "@cynthai/icons";
import type { ReactNode } from "react";
import { tv } from "tailwind-variants";

import { CustomTooltip } from "./CustomTooltip";
import { TooltipTarget } from "./TooltipTarget";
import type { SlotClassNames } from "./types";

interface InfoTooltipSlots {
  readonly trigger: string;
  readonly card: string;
}

interface SharedInfoTooltipProps {
  readonly iconSize?: number;
  readonly classNames?: SlotClassNames<InfoTooltipSlots>;
}

interface TitledInfoTooltipProps extends SharedInfoTooltipProps {
  readonly title: string;
  readonly description: string;
  readonly content?: undefined;
  readonly "aria-label"?: string;
}

interface CustomInfoTooltipProps extends SharedInfoTooltipProps {
  readonly content: ReactNode;
  readonly title?: undefined;
  readonly description?: undefined;
  readonly "aria-label": string;
}

type InfoTooltipProps = TitledInfoTooltipProps | CustomInfoTooltipProps;

const infoTooltipStyles = tv({
  slots: {
    trigger:
      "text-text-secondary hover:text-text-primary inline-flex items-center justify-center",
    card: "border-border bg-background-2 w-72 rounded-lg border px-3 pt-2 pb-3 shadow-lg",
  },
});

// the arrow points at the trigger sitting to the tooltip's left, so the
// wrapper keeps overflow visible and reserves two stripes of left padding
export function InfoTooltip(props: Readonly<InfoTooltipProps>) {
  const {
    title,
    description,
    content,
    "aria-label": ariaLabel,
    iconSize = 16,
    classNames,
  } = props;
  const { trigger, card } = infoTooltipStyles();

  return (
    <CustomTooltip
      content={
        <div className="relative overflow-visible pl-2">
          <div
            className="border-r-border absolute top-3.5 left-0 h-0 w-0 border-y-[8px] border-r-[8px] border-y-transparent"
            aria-hidden
          />
          <div
            className="border-r-background-2 absolute top-4 left-px h-0 w-0 border-y-[7px] border-r-[7px] border-y-transparent"
            aria-hidden
          />
          <div className={card({ className: classNames?.card })}>
            {content ?? (
              <>
                <p className="text-text-primary text-sm font-medium">{title}</p>
                <p className="text-text-tertiary mt-1.5 text-xs/relaxed">
                  {description}
                </p>
              </>
            )}
          </div>
        </div>
      }
      variant="unstyled"
      placement="right top"
      offset={10}
      containerPadding={16}
    >
      <TooltipTarget
        aria-label={ariaLabel ?? title}
        className={trigger({ className: classNames?.trigger })}
      >
        <InfoIcon width={iconSize} height={iconSize} className="shrink-0" />
      </TooltipTarget>
    </CustomTooltip>
  );
}
